import { useEffect } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toCamel } from './case';
import { getCompany } from './companyState';
import { supabase } from './supabase';

/* ---- đăng ký realtime dùng chung: mỗi bảng chỉ một kênh, dù nhiều màn hình cùng dùng ---- */
const channels = new Map<string, { n: number; stop: () => void }>();
/** Khung nhìn (view) không có sự kiện realtime: làm mới cùng lúc với bảng gốc. */
const ALSO: Record<string, string[]> = { employees: ['employee_directory'] };

function subscribeTable(table: string, qc: QueryClient): () => void {
  let entry = channels.get(table);
  if (!entry) {
    // Gộp các sự kiện dồn dập (vd. import hàng nghìn dòng) thành một lần tải lại.
    let timer: ReturnType<typeof setTimeout> | undefined;
    const ch = supabase().channel('rt-' + table).on('postgres_changes', { event: '*', schema: 'public', table }, () => {
      clearTimeout(timer);
      timer = setTimeout(() => { for (const k of [table, ...(ALSO[table] ?? [])]) void qc.invalidateQueries({ queryKey: [k] }); }, 400);
    }).subscribe();
    entry = { n: 0, stop: () => { clearTimeout(timer); void supabase().removeChannel(ch); } };
    channels.set(table, entry);
  }
  entry.n++;
  return () => {
    const e = channels.get(table);
    if (e && --e.n <= 0) { e.stop(); channels.delete(table); }
  };
}

export function useRealtime(table: string): void {
  const qc = useQueryClient();
  useEffect(() => subscribeTable(table, qc), [table, qc]);
}

export interface RowsOptions {
  order?: string; ascending?: boolean; enabled?: boolean; /** bảng để nghe realtime (mặc định chính bảng đang đọc) */ realtime?: string;
  /** Cột phụ để phân trang ổn định (mặc định 'id'). Bảng KHÔNG có cột id (chấm công, bảng lương: khoá chính là đơn vị + tháng) phải đặt null, nếu không truy vấn lỗi 42703. */
  tie?: string | null;
}

const PAGE = 1000;   // Supabase/PostgREST mặc định trả tối đa 1000 dòng mỗi lần → đọc từng trang cho đến hết

export async function fetchAllRows(table: string, order?: string, ascending = true, tie: string | null = 'id'): Promise<Record<string, unknown>[]> {
  const all: Record<string, unknown>[] = [];
  for (let from = 0; ; from += PAGE) {
    let q = supabase().from(table).select('*');
    if (order) q = q.order(order, { ascending });
    if (tie) q = q.order(tie, { ascending: true });                          // khóa phụ để phân trang ổn định
    q = q.range(from, from + PAGE - 1);
    const { data, error } = await q;
    if (error) throw error;
    all.push(...(data as Record<string, unknown>[]));
    if (!data || data.length < PAGE) break;
  }
  return all;
}

/** Đọc cả bảng (đã qua RLS), đổi tên cột sang camelCase, tự làm mới khi dữ liệu đổi (realtime). */
export function useRows<T>(table: string, opts: RowsOptions = {}) {
  useRealtime(opts.realtime ?? table);
  return useQuery({
    queryKey: [table],
    enabled: opts.enabled ?? true,
    queryFn: async (): Promise<T[]> => (await fetchAllRows(table, opts.order, opts.ascending, opts.tie === undefined ? 'id' : opts.tie)).map(r => toCamel<T>(r)),
  });
}

/* ---- thiết lập dùng chung (app_settings) ---- */
export type CompanySettings = Record<string, unknown> & {
  nameVi?: string; nameEn?: string; appUrl?: string; mailApp?: string; mailCc?: string; docOrg?: string;
};

/** Các khoá thiết lập riêng của từng đơn vị (đơn vị chính giữ khoá gốc, đơn vị khác dùng khoá "<id>/<khoá>"). */
export const UNIT_KEYS = new Set(['company', 'numbering', 'payroll', 'payVers', 'kpiSchemes', 'salScale', 'labor', 'cash', 'bankList', 'rxMaps', 'eqCats']);
/** Trường trong thiết lập "company" dùng chung toàn hệ thống (luôn lấy từ đơn vị chính). */
export const GLOBAL_COMPANY_FIELDS = ['mailApp', 'mailCc', 'appUrl', 'rate'];

export const settingKey = (key: string, co: string = getCompany()): string => (co === 'main' || !UNIT_KEYS.has(key) ? key : `${co}/${key}`);

async function readSetting(key: string): Promise<Record<string, unknown>> {
  const { data, error } = await supabase().from('app_settings').select('value').eq('key', key).maybeSingle();
  if (error) throw error;
  return ((data?.value as Record<string, unknown>) ?? {});
}

/**
 * Thiết lập của đơn vị `co` (mặc định: đơn vị đang làm việc). Với "company" của đơn vị khác, các trường dùng chung
 * (tỷ giá, cấu hình thư…) lấy từ đơn vị chính.
 */
export function useSetting<T extends Record<string, unknown> = CompanySettings>(key: string, co?: string) {
  useRealtime('app_settings');
  return useQuery({
    queryKey: ['app_settings', key, co ?? ''],
    queryFn: async (): Promise<T> => {
      const c = co ?? getCompany();
      const own = await readSetting(settingKey(key, c));
      if (key !== 'company' || c === 'main') return own as T;
      const main = await readSetting('company');
      return { ...Object.fromEntries(GLOBAL_COMPANY_FIELDS.filter(k => k in main).map(k => [k, main[k]])), ...own } as T;
    },
  });
}

/** Gộp `patch` vào thiết lập hiện có của đơn vị `co` (mặc định: đơn vị đang làm việc) rồi ghi lại (đọc–sửa–ghi). */
export async function saveSetting(key: string, patch: Record<string, unknown>, updatedBy = '', co?: string): Promise<void> {
  const c = co ?? getCompany();
  const write = async (k: string, p: Record<string, unknown>) => {
    if (!Object.keys(p).length) return;
    const value = { ...(await readSetting(k)), ...p };
    const { error } = await supabase().from('app_settings').upsert({ key: k, value, updated_by: updatedBy }, { onConflict: 'key' });
    if (error) throw error;
  };
  if (key === 'company' && c !== 'main') {
    const glob = Object.fromEntries(Object.entries(patch).filter(([k]) => GLOBAL_COMPANY_FIELDS.includes(k)));
    const own = Object.fromEntries(Object.entries(patch).filter(([k]) => !GLOBAL_COMPANY_FIELDS.includes(k)));
    await write(settingKey(key, c), own);
    await write('company', glob);
    return;
  }
  await write(settingKey(key, c), patch);
}

/** Ghi nhiều dòng theo lô (upsert theo `onConflict`), báo tiến độ. Trả về số dòng đã ghi. */
export async function upsertChunks(table: string, rows: Record<string, unknown>[], onConflict: string, onProgress?: (done: number, total: number) => void, size = 200): Promise<number> {
  let done = 0;
  for (let i = 0; i < rows.length; i += size) {
    const part = rows.slice(i, i + size);
    const { error } = await supabase().from(table).upsert(part, { onConflict });
    if (error) throw error;
    done += part.length;
    onProgress?.(done, rows.length);
  }
  return done;
}

const HAS_VI = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i;

interface DbErrorLike { message?: string; code?: string; details?: string }

/** Đổi lỗi kỹ thuật của CSDL thành câu tiếng Việt dễ hiểu. Lỗi do chính hệ thống ném ra (đã là tiếng Việt) giữ nguyên. */
export function errMsg(e: unknown, fallback = 'Có lỗi xảy ra, thử lại sau.'): string {
  const x = (typeof e === 'object' && e ? e : {}) as DbErrorLike;
  const msg = x.message || (e instanceof Error ? e.message : '');
  if (!msg) return fallback;
  if (HAS_VI.test(msg)) return msg;                      // thông báo do chính hệ thống ném ra (đã là tiếng Việt): giữ nguyên
  if (x.code === '42501' && /row-level security|permission denied/i.test(msg)) return 'Bạn không có quyền thực hiện thao tác này.';
  if (/permission denied/i.test(msg)) return 'Bạn không có quyền thực hiện thao tác này.';
  if (x.code === '23505' || /duplicate key/i.test(msg)) return 'Dữ liệu bị trùng (mã hoặc khóa đã tồn tại).';
  if (x.code === '23503' && /still referenced|violates foreign key/i.test(msg)) return 'Đang được dùng ở nơi khác (chứng từ, hợp đồng…), không xoá được.';
  if (/Failed to fetch|NetworkError|network/i.test(msg)) return 'Mất kết nối mạng. Thử lại sau.';
  if (/^(new row|null value|invalid input|value too long|check constraint)/i.test(msg) || /violates (check|not-null)/i.test(msg)) return 'Dữ liệu không hợp lệ, kiểm tra lại các ô đã nhập.';
  return msg;
}
